import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Trash2, Pencil, Shield, Copy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { toast } from '@/components/ui/Toast';
import { useRoles, useDeleteRole } from './api';
import type { AccessRole } from '@/lib/api/accessManager';
import { RoleFormDialog } from './components/RoleFormDialog';
import { useAuthStore } from '@/lib/auth/authStore';
import { useCanAccess } from '@/lib/permissions/useCanAccess';
import { useCurrentPlan } from '@/lib/permissions/usePlanQuota';
import { UpgradeDialog } from '@/features/billing/UpgradeDialog';
import { useT } from '@/lib/i18n/useT';

export function RolesTab() {
  const sessionApp = useAuthStore((s) => s.app) ?? undefined;
  const t = useT();
  const { data, isLoading } = useRoles({ appType: sessionApp });
  const deleteRole = useDeleteRole();
  const canCreate = useCanAccess('Role', 'create');
  const canEdit = useCanAccess('Role', 'edit');
  const canDelete = useCanAccess('Role', 'delete');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [editing, setEditing] = useState<AccessRole | null>(null);
  const [cloneFrom, setCloneFrom] = useState<AccessRole | null>(null);
  const { data: planUsage } = useCurrentPlan();
  // Use the live `rolesUsed` from `/plans/current` (computed via COUNT(*) on
  // agency roles) — the access-manager listing is scoped and may under-count.
  const rolesLimit = planUsage?.maxRoles ?? 0;
  const rolesUsed =
    planUsage?.rolesUsed ?? data?.data?.total ?? data?.data?.items?.length ?? 0;
  const rolesReached = rolesLimit > 0 && rolesUsed >= rolesLimit;

  function openCreate() {
    if (rolesReached) {
      setUpgradeOpen(true);
      return;
    }
    setEditing(null);
    setCloneFrom(null);
    setDialogOpen(true);
  }
  function openEdit(r: AccessRole) {
    setEditing(r);
    setCloneFrom(null);
    setDialogOpen(true);
  }
  function openClone(r: AccessRole) {
    setEditing(null);
    setCloneFrom(r);
    setDialogOpen(true);
  }

  // Deep-link from other tabs: ?editRole=<id> auto-opens the matching role.
  // Templates open in "view" intent — the dialog itself is the read-only path.
  const [searchParams, setSearchParams] = useSearchParams();
  const editRoleId = searchParams.get('editRole');
  const items = data?.data?.items ?? [];
  useEffect(() => {
    if (!editRoleId || dialogOpen) return;
    const target = items.find((r) => String(r.id) === editRoleId);
    if (!target) return;
    openEdit(target);
    // Strip the param so subsequent navigations don't keep reopening it.
    const params = new URLSearchParams(searchParams);
    params.delete('editRole');
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRoleId, items]);

  function onDelete(r: AccessRole) {
    if (!confirm(t('roles.confirmDelete', { name: r.name }))) return;
    deleteRole.mutate(r.id, {
      onSuccess: () => toast({ tone: 'success', title: t('roles.deletedToast') }),
      onError: (e: Error) => toast({ tone: 'error', title: e.message }),
    });
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle>{t('roles.title')}</CardTitle>
        {canCreate && (
          <Button
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={openCreate}
          >
            {t('roles.createRole')}
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-6"><Skeleton className="h-40" /></div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Shield className="h-6 w-6" />}
            title={t('roles.empty.title')}
            description={t('roles.empty.body')}
            action={
              canCreate ? (
                <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openCreate}>
                  {t('roles.createRole')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ul className="divide-y divide-[var(--color-border-subtle)]">
            {items.map((r) => (
              <li
                key={r.id}
                className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-[var(--color-bg-elevated)]"
              >
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 font-bold">
                    {r.name}
                    <Badge tone="accent">{r.type}</Badge>
                    {r.companyId == null && <Badge tone="purple">{t('roles.templateBadge')}</Badge>}
                  </p>
                  {r.description && (
                    <p className="mt-0.5 line-clamp-2 text-[12px] text-[var(--color-fg-default)]">
                      {r.description}
                    </p>
                  )}
                  <p className="mt-0.5 text-xs text-[var(--color-fg-muted)]">
                    {t('roles.permsCount', { count: r.permissions?.length ?? 0 })}
                    {(() => {
                      const names =
                        r.scopes && r.scopes.length > 0
                          ? r.scopes.map((s) => s.scopedCompany?.name).filter(Boolean)
                          : r.company
                            ? [r.company.name]
                            : [];
                      return names.length > 0 ? ` · ${names.join(', ')}` : '';
                    })()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {canCreate && (
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<Copy className="h-3.5 w-3.5" />}
                      onClick={() => openClone(r)}
                      title={t('roles.cloneTitle')}
                    >
                      {t('roles.clone')}
                    </Button>
                  )}
                  {r.companyId == null ? (
                    <span
                      className="text-[10px] text-[var(--color-fg-subtle)] italic"
                      title={t('roles.readOnlyTitle')}
                    >
                      {t('roles.readOnlyLabel')}
                    </span>
                  ) : (
                    <>
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Pencil className="h-3.5 w-3.5" />}
                          onClick={() => openEdit(r)}
                        >
                          {t('common.edit')}
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                          onClick={() => onDelete(r)}
                        >
                          {t('common.delete')}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <RoleFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        cloneFrom={cloneFrom}
        onQuotaExceeded={() => setUpgradeOpen(true)}
      />
      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        resource="maxRoles"
        currentPlanName="Free"
        limit={rolesLimit}
      />
    </Card>
  );
}
